// Global
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useEffect, useRef, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
// Components
import Component from 'src/helpers/Component/Component';

import classNames from 'classnames';
import { ComponentProps } from 'lib/types/component-props';
import BodyCopy from 'src/helpers/BodyCopy/BodyCopy';
import Headline from 'src/helpers/Headline/Headline';

export type TechDocChatbotProps =
  Feature.EnterpriseWeb.AndersenWindows.Components.Tool.TechDocChatbot.TechDocChatbot &
    ComponentProps;

const formatPages = (pages: SourceRef['page_numbers']) => {
  if (Array.isArray(pages)) {
    return pages.join(', ');
  }
  if (typeof pages === 'string') {
    return pages;
  }
  return '';
};

interface SourceRef {
  file_name?: string | null;
  page_numbers?: number[] | string | null;
  raw_text?: string | null; // optional preview
  url?: string | null; // future-proof if backend adds links
}

interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  sources?: SourceRef[];
  backendMessageId?: number;
  backendSessionId?: number;
  feedback?: 'up' | 'down' | undefined;
  feedbackPending?: boolean;
  feedbackReason?: string | null;
  isThinking?: boolean;
}

const TechDocChatbot = (props: TechDocChatbotProps) => {
  const [sessionId, setSessionId] = useState<number | undefined>(undefined);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      text: "Hello! I'm your TechDoc assistant. I can help you with questions about Andersen Windows & Doors products, technical documentation, and installation procedures. What would you like to know?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const SHOW_SOURCES = false;

  const [feedbackModal, setFeedbackModal] = useState<{
    open: boolean;
    messageId?: string;
    text: string;
  }>({ open: false, messageId: undefined, text: '' });

  async function postFeedback(opts: {
    messageId: number;
    isPositive: boolean;
    commentText?: string | null;
    reasonCode?: string | null;
  }) {
    const body = {
      message_id: opts.messageId,
      is_positive: opts.isPositive,
      comment_text: opts.commentText ?? null,
      reason_code: opts.reasonCode ?? null,
      session_id: sessionId ?? null,
    };

    const resp = await fetch('/api/ai/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await resp.json();
    if (!resp.ok) {
      throw new Error(data?.detail || data?.error || `Feedback failed (${resp.status})`);
    }
    return data;
  }

  function setMessage(idx: number, patch: Partial<ChatMessage>) {
    setMessages((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  function patchMessageById(id: string, patch: Partial<ChatMessage>) {
    setMessages((prev) => {
      const i = prev.findIndex((m) => m.id === id);
      if (i === -1) {
        return prev;
      }
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  }

  async function handleThumbUp(idx: number) {
    const m = messages[idx];
    if (!m.backendMessageId) {
      return;
    }

    try {
      setMessage(idx, { feedbackPending: true });

      // If flipping from down -> up, send empty/null reason/comment to clear it
      await postFeedback({
        messageId: m.backendMessageId,
        isPositive: true,
        commentText: null,
        reasonCode: null,
      });

      setMessage(idx, { feedback: 'up', feedbackPending: false, feedbackReason: null });
    } catch (e) {
      console.error(e);
      setMessage(idx, { feedbackPending: false });
    }
  }

  function handleThumbDown(idx: number) {
    const m = messages[idx];
    if (!m?.backendMessageId) {
      return;
    }
    setFeedbackModal({ open: true, messageId: m.id, text: m?.feedbackReason ?? '' });
  }

  async function submitThumbDown(withoutExplanation = false) {
    const id = feedbackModal.messageId!;
    const idx = messages.findIndex((x) => x.id === id);
    if (idx < 0) {
      return;
    }
    const m = messages[idx];
    if (!m?.backendMessageId) {
      return;
    }

    try {
      setMessage(idx, { feedbackPending: true });

      const explanation = withoutExplanation ? null : feedbackModal.text.trim() || null;

      await postFeedback({
        messageId: m.backendMessageId,
        isPositive: false,
        // ReasonCode optional; you could map to a small set later. For now leave null.
        reasonCode: null,
        commentText: explanation,
      });

      setMessage(idx, { feedback: 'down', feedbackPending: false, feedbackReason: explanation });
    } catch (e) {
      console.error(e);
      setMessage(idx, { feedbackPending: false });
    } finally {
      setFeedbackModal({ open: false, messageId: undefined, text: '' });
    }
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!inputText.trim()) {
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputText.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const question = inputText.trim();
    setInputText('');
    setIsLoading(true);

    const thinkingId = `thinking-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: thinkingId,
        text: 'Thinking...',
        isUser: false,
        timestamp: new Date(),
        isThinking: true,
      },
    ]);

    try {
      const payload = { question, verbose: false, session_id: sessionId ?? null };
      console.log('POST /ai/answer payload', payload);

      const resp = await fetch('/api/ai/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await resp.json();
      if (!resp.ok) {
        const errMsg = data?.detail || data?.error || `Upstream error (${resp.status})`;
        throw new Error(errMsg);
      }

      if (!sessionId || sessionId !== data.session_id) {
        setSessionId(data.session_id);
      }

      const responseText = data?.answer || 'No response content.';
      const sourceDocs = Array.isArray(data?.sources)
        ? data.sources
        : Array.isArray(data?.source_documents)
        ? data.source_documents
        : [];

      patchMessageById(thinkingId, {
        id: (Date.now() + 1).toString(), // give it a new stable id if you want
        text: responseText,
        isThinking: false,
        sources: sourceDocs,
        backendMessageId: data.message_id,
        backendSessionId: data.session_id,
      });
    } catch (error) {
      console.error('API Error:', error);
      patchMessageById(thinkingId, {
        text: `Backend Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isThinking: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!props.fields) {
    return <></>;
  }

  return (
    <Component
      variant="full"
      backgroundVariant="white"
      sectionWrapperClasses=""
      dataComponent="tool/techdocchatbot"
      {...props}
    >
      <div className="col-span-2 md:col-span-12">
        <Headline {...props} />
        <BodyCopy {...props} />

        {/* Chatbot Interface - Exact Figma Design */}
        <div
          className="mx-auto mt-6 flex h-[750px] w-full max-w-sm flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          style={{ width: '400px' }}
        >
          {/* Chat Header - Orange #ED6833 */}
          <div
            className="flex items-center justify-between p-4 text-white"
            style={{ backgroundColor: '#ED6833', height: '88px' }}
          >
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-bold text-white">Tech Doc ChatBot</h3>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: '#43EE7D' }}></div>
              <span className="text-sm font-medium" style={{ color: '#43EE7D' }}>
                Online
              </span>
              <button className="flex h-8 w-8 items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-white p-4">
            {messages.map((message, idx) => (
              <div
                key={message.id}
                className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
              >
                {!message.isUser && (
                  <div className="mr-3 mt-2 flex-shrink-0">
                    <img
                      src="/andersen-logo.svg"
                      alt="Andersen Logo"
                      className="h-12 w-12"
                      style={{ width: '57px', height: '57px' }}
                    />
                  </div>
                )}

                <div
                  className={`max-w-sm rounded-2xl px-4 py-3 shadow-sm ${
                    message.isUser ? 'bg-gray-100 text-gray-800' : 'text-gray-800 bg-white'
                  }`}
                  style={
                    message.isUser
                      ? {}
                      : { border: '2px solid #ED6833', backgroundColor: '#FEF3EE' }
                  }
                >
                  {message.isThinking ? (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 animate-pulse text-sm font-medium">
                        Thinking…
                      </span>
                    </div>
                  ) : (
                    <p
                      className={classNames(
                        'text-gray-800',
                        'text-sm',
                        'leading-relaxed',
                        '[&_a]:font-semibold',
                        '[&_a]:text-inherit',
                        '[&_a]:underline',
                        '[&_a]:decoration-[#ED6833]',
                        '[&_a]:underline-offset-2',
                        '[&_a:hover]:opacity-80'
                      )}
                      dangerouslySetInnerHTML={{ __html: message.text }}
                    />
                  )}
                  {SHOW_SOURCES && !message.isUser && message.sources?.length ? (
                    <details className="mt-3">
                      <summary
                        className="cursor-pointer text-xs font-semibold"
                        style={{ color: '#ED6833' }}
                      >
                        Sources ({message.sources.length})
                      </summary>
                      <ul className="text-gray-700 mt-2 max-h-40 space-y-1 overflow-y-auto pr-1 text-xs">
                        {message.sources.map((s, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span
                              className="mt-1 inline-block h-1.5 w-1.5 rounded-full"
                              style={{ backgroundColor: '#ED6833' }}
                            />
                            <div className="leading-snug">
                              <div className="font-medium">
                                {s.url ? (
                                  <a
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline decoration-[#ED6833] underline-offset-2"
                                  >
                                    {s.file_name || 'Document'}
                                  </a>
                                ) : (
                                  s.file_name || 'Document'
                                )}
                                {s.page_numbers ? (
                                  <span className="text-gray-600">
                                    {' '}
                                    — p. {formatPages(s.page_numbers)}
                                  </span>
                                ) : null}
                              </div>
                              {s.raw_text ? (
                                <div className="text-gray-600 mt-1 whitespace-pre-wrap text-[11px] line-clamp-3">
                                  {s.raw_text}
                                </div>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                  {!message.isUser && !message.isThinking && message.backendMessageId && (
                    <div className="mt-2 flex items-center justify-end gap-2 text-black">
                      <button
                        aria-label="Thumbs up"
                        aria-pressed={message.feedback === 'up'}
                        onClick={() => handleThumbUp(idx)}
                        disabled={message.feedbackPending}
                        className={`rounded-full p-1 transition-opacity ${
                          message.feedback === 'up' ? 'opacity-100' : 'opacity-70 hover:opacity-100'
                        }`}
                        title={
                          message.feedback === 'up' ? 'You marked this helpful' : 'Mark helpful'
                        }
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill={message.feedback === 'up' ? '#000' : 'none'}
                          stroke="#000"
                          strokeWidth="2"
                        >
                          <path d="M14 9V5a3 3 0 0 0-3-3l-1 7H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h7l5-8V9z" />
                        </svg>
                      </button>

                      <button
                        aria-label="Thumbs down"
                        aria-pressed={message.feedback === 'down'}
                        onClick={() => handleThumbDown(idx)}
                        disabled={message.feedbackPending}
                        className={`rounded-full p-1 transition-opacity ${
                          message.feedback === 'down'
                            ? 'opacity-100'
                            : 'opacity-70 hover:opacity-100'
                        }`}
                        title={
                          message.feedback === 'down'
                            ? 'You marked this unhelpful'
                            : 'Mark unhelpful'
                        }
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill={message.feedback === 'down' ? '#000' : 'none'}
                          stroke="#000"
                          strokeWidth="2"
                        >
                          <path d="M10 15v4a3 3 0 0 0 3 3l1-7h5a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-7l-5 8v2z" />
                        </svg>
                      </button>

                      {message.feedbackPending && (
                        <span className="text-gray-500 ml-1 text-[11px]">saving…</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div aria-hidden style={{ height: 8 }} />
            <div ref={messagesEndRef} />
          </div>

          {/* Action Buttons */}
          <div className="px-4 py-2" style={{ backgroundColor: '#F3F5F6' }}>
            <div className="flex space-x-2">
              <button
                className="text-gray-700 hover:bg-gray-300 flex-1 rounded-full py-2 px-4 text-sm font-medium transition-colors"
                style={{ backgroundColor: '#F3F5F6', border: '0.5px solid #444444' }}
              >
                Find another document
              </button>
              <button
                className="text-gray-700 hover:bg-gray-300 flex-1 rounded-full py-2 px-4 text-sm font-medium transition-colors"
                style={{ backgroundColor: '#F3F5F6', border: '0.5px solid #444444' }}
              >
                Contact an Expert
              </button>
            </div>
          </div>
          {feedbackModal.open && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
              <div className="w-[420px] rounded-2xl bg-white p-4 shadow-xl">
                <h4 className="mb-2 text-base font-semibold">Tell us what went wrong (optional)</h4>
                <textarea
                  value={feedbackModal.text}
                  onChange={(e) => setFeedbackModal((s) => ({ ...s, text: e.target.value }))}
                  placeholder="What was inaccurate, missing, or unhelpful?"
                  className="border-gray-300 h-28 w-full resize-y rounded-md border px-3 py-2 text-sm leading-6 outline-none focus:border-[#ED6833]"
                />
                <div className="mt-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() =>
                      setFeedbackModal({ open: false, messageId: undefined, text: '' })
                    }
                    className="border-gray-400 text-gray-700 hover:bg-gray-100 rounded-full border px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => submitThumbDown(true)}
                    className="border-gray-400 text-gray-700 hover:bg-gray-100 rounded-full border px-4 py-2 text-sm"
                  >
                    Submit without explanation
                  </button>
                  <button
                    onClick={() => submitThumbDown(false)}
                    className="rounded-full bg-[#ED6833] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Input Area - Bottom */}
          <div className="bg-white p-4">
            <div
              className="border-gray-300 flex items-center space-x-3 rounded-full border bg-white px-4 py-3"
              style={{ height: '64px' }}
            >
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyPress} // use onKeyDown; onKeyPress is deprecated
                placeholder="Type your message here..."
                className="text-gray-900 placeholder-gray-500 flex-1 text-sm focus:outline-none"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isLoading}
                className="flex-shrink-0 text-[#ED6833] transition-colors disabled:opacity-50"
              >
                <img src="/vuesax/linear/send.svg" alt="Send" width="24" height="24" />
              </button>
            </div>
          </div>

          {/* Support Footer */}
          <div className="bg-white px-4 pb-4">
            <p className="text-gray-600 text-center text-xs">
              Contact us at{' '}
              <a
                href="tel:18009333626"
                className="font-semibold underline decoration-[#ED6833] underline-offset-2"
                aria-label="Call Andersen support at 1 800 933 3626"
              >
                1-800-933-3626
              </a>
            </p>
          </div>
        </div>
      </div>
    </Component>
  );
};

export default withDatasourceCheck()<TechDocChatbotProps>(TechDocChatbot);
